import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Form, 
  Dropdown, 
  Button, 
  Badge,
  Spinner,
  Modal,
  Alert,
  ListGroup
} from 'react-bootstrap';
import { 
  FaClipboardList, 
  FaMoneyBillWave, 
  FaUserCircle, 
  FaSignOutAlt, 
  FaPlus,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaBoxes,
  FaCheckCircle,
  FaExchangeAlt,
  FaRupeeSign,
  FaFileAlt,
  FaSearch,
  FaTimes,
  FaFileUpload
} from 'react-icons/fa';
import { Link, useNavigate, Outlet } from 'react-router-dom';
import { agentApi, productApi, notificationApi, searchApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import Select from 'react-select';
import Swal from 'sweetalert2';
import AgentSidebar from '../../components/AgentSidebar';
import SearchResults from '../../components/SearchResults';
import BulkUploadModal from '../../components/Leads/BulkUploadModal';
import './Dashboard.css';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const { getCurrentUser, logout } = useAuth();
  const currentUser = getCurrentUser();

  // Dashboard data state
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState({
    total_active_leads: 0,
    total_applications: 0,
    total_disbursement: 0,
    total_earnings: 0
  });
  
  // Leads table state
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [loadingLeads, setLoadingLeads] = useState(true);
  
  // Filters state
  const [filters, setFilters] = useState({
    status: null,
    product_id: null,
    search: "",
    skip: 0,
    limit: 10
  });
  
  // Product and status options state
  const [productOptions, setProductOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([
    { code: 1, name: "Requested" },
    { code: 2, name: "In Progress" },
    { code: 3, name: "Disbursed" },
    { code: 4, name: "Rejected" }
  ]);

  // Add lead modal state
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    mobile: '',
    case_type: '1',
    product_id: ''
  });
  const [validated, setValidated] = useState(false);
  const [addingLead, setAddingLead] = useState(false);
  const [addLeadError, setAddLeadError] = useState('');
  
  // Bulk upload modal state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  
  // Notifications state (from RM Dashboard)
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Search state (from RM Dashboard)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchRef = useRef(null);
  
  // Function to fetch leads that can be called from other places (like after adding a new lead)
  const fetchLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      console.log('Fetching leads with filters:', filters);
      const response = await agentApi.getLeads({
        skip: filters.skip,
        limit: filters.limit,
        status: filters.status,
        product_id: filters.product_id,
        search: filters.search
      });
      
      console.log('Full API response:', JSON.stringify(response.data, null, 2));
      
      if (response.data.items && response.data.items.length > 0) {
        const firstItem = response.data.items[0];
        console.log('First lead item:', firstItem);
        // Log specific properties to check their existence
        console.log('Lead user check:', firstItem.lead_user);
        console.log('User check:', firstItem.user);
        console.log('CIBIL score in lead:', firstItem.cibil_score);
        if (firstItem.lead_user) {
          console.log('CIBIL score in lead_user:', firstItem.lead_user.cibil_score);
        }
        if (firstItem.user) {
          console.log('CIBIL score in user:', firstItem.user.cibil_score);
        }
      }
      
      setLeads(response.data.items || []);
      setTotalLeads(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoadingLeads(false);
    }
  }, [filters]); 
  
  // Fetch leads when filters change
  useEffect(() => {
    fetchLeads();
  }, [filters, fetchLeads]); 
  
  // Set document title and fetch dashboard data
  useEffect(() => {
    document.title = 'Agent Dashboard';
    
    // Fetch summary data
    const fetchSummaryData = async () => {
      try {
        const response = await agentApi.getLeadsSummary();
        setSummaryData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching summary data:", error);
        setLoading(false);
      }
    };
    
    // Fetch product options
    const fetchProductOptions = async () => {
      try {
        setProductOptions([
          { id: 1, name: "Credit Card" },
          { id: 2, name: "Personal Loan" },
          { id: 3, name: "Home Loan" },
          { id: 4, name: "Business Loan" },
          {id:5, name:"LAP"}
        ]);
      } catch (error) {
        console.error("Error fetching product options:", error);
      }
    };
    
    fetchSummaryData();
    fetchProductOptions();
    fetchProducts(); 
    fetchLeads(); 
  }, [fetchLeads]);
  
  // Notifications polling effect
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsData(); // Initial fetch for count
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every 60 seconds

    return () => clearInterval(intervalId); // Cleanup interval
  }, []); // Run once for interval setup

  // Fetch full notifications specifically when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 }); // Fetch latest 10
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications for dropdown:', error);
          setNotifications([]); // Clear notifications on error
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    fetchFullNotifications();
  }, [showNotifications]); // Trigger when dropdown visibility changes

  // Handle clicks outside notification dropdown and search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target) && !showSearchModal) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchModal]);
  
  // Search with debounce
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If search query is empty, clear results
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    // Set a new timeout for search
    const timeout = setTimeout(() => {
      performSearch(searchQuery);
    }, 500); // 500ms debounce
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchQuery]);
  
  // Fetch products for add lead modal
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await productApi.getProducts();
      setProducts(response.data.items || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };
  
  // Handle new lead form changes
  const handleNewLeadChange = (e) => {
    const { name, value } = e.target;
    setNewLead(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle select change for product in add lead form
  const handleSelectChange = (field, selectedOption) => {
    setNewLead(prev => ({
      ...prev,
      [field]: selectedOption ? selectedOption.value : ''
    }));
  };
  
  // Handle add lead form submission
  const handleAddLead = async (e) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    setValidated(true);
    setAddingLead(true);
    setAddLeadError('');
    
    try {
      const leadData = {
        ...newLead,
        agent_id: currentUser?.agent_profile?.id || currentUser?.id
      };
      
      const response = await agentApi.addLead(leadData);
      
      setAddingLead(false);
      setShowAddLeadModal(false);
      
      setNewLead({
        name: '',
        email: '',
        mobile: '',
        case_type: '1',
        product_id: ''
      });
      setValidated(false);
      
      Swal.fire({
        icon: 'success',
        title: 'Lead Added Successfully',
        text: `${newLead.name} has been added to your leads.`,
        confirmButtonText: 'OK'
      });
      
      fetchLeads();
      
    } catch (err) {
      console.error('Error adding lead:', err);
      setAddingLead(false);
      setAddLeadError(err.response?.data?.detail || 'An error occurred while adding the lead.');
    }
  };
  
  // Handler for bulk upload submission
  const handleBulkUploadSubmit = useCallback(async (file) => {
    console.log('Submitting bulk upload for agent');
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const agentId = currentUser?.agent_profile?.id || currentUser?.id;
      if (agentId) {
        formData.append('agent_id', agentId);
      }

      const response = await agentApi.bulkUploadLeads(formData);

      // Close modal on success
      setShowBulkUploadModal(false);

      // Refresh leads list
      fetchLeads();

      // Show success message with counts (no timer, requires user confirmation)
      Swal.fire({
        icon: 'success',
        title: 'Bulk Upload Successful',
        text: `Successfully processed ${response.data.total_records} leads: ${response.data.successful} created, ${response.data.duplicates} duplicates, ${response.data.skipped} skipped, ${response.data.failed} failed.`,
        showConfirmButton: true,
        confirmButtonText: 'OK'
      });

    } catch (err) {
      console.error('Error in bulk upload:', err);
      // Re-throw the error so the modal can display it
      throw new Error(err.response?.data?.detail || err.message || 'Bulk upload failed.');
    }
  }, [currentUser, fetchLeads]);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, search: value, skip: 0 }));
  };
  
  // Handle search form submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Search is already handled by the useEffect
  };
  
  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setFilters(prev => ({ ...prev, [type]: value, skip: 0 }));
  };
  
  // Handle pagination
  const handlePageChange = (newSkip) => {
    if (newSkip >= 0 && newSkip < totalLeads) {
      setFilters(prev => ({ ...prev, skip: newSkip }));
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Helper function to get status color
  const getStatusColor = (statusCode) => {
    switch (parseInt(statusCode)) {
      case 1:
        return '#17a2b8'; 
      case 2:
        return '#ffc107'; 
      case 3:
        return '#28a745'; 
      case 4:
        return '#dc3545'; 
      default:
        return '#6c757d'; 
    }
  };
  
  // Function to determine badge color based on CIBIL score
  const getCibilScoreBadgeColor = (score) => {
    if (!score) return 'secondary';
    if (score >= 750) return 'success';
    if (score >= 650) return 'info';
    if (score >= 550) return 'warning';
    return 'danger';
  };
  
  // Notification handlers
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };
  
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    // Only proceed if the notification exists and is unread
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      // Decrement count optimistically ONLY if it was unread
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    // Only proceed if there are unread notifications
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };
  
  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; } // Fallback
  };
  
  // Search handlers
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };
  
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && searchQuery.trim().length > 1) {
      performSearch(searchQuery);
      // Show modal for mobile view
      if (window.innerWidth < 768) {
        setShowSearchModal(true);
      }
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };
  
  const handleSearchResultClick = () => {
    setShowSearchResults(false);
    setShowSearchModal(false);
  };

  // Handle checking CIBIL for a lead
  const handleCheckCibilRequest = async (leadId) => {
    try {
      Swal.fire({
        title: 'Checking CIBIL Score',
        text: 'Please wait while we fetch the CIBIL data...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      const response = await agentApi.requestCibilCheck(leadId);
      
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'CIBIL Check Successful',
          text: `CIBIL Score: ${response.data.cibil_score}`,
          confirmButtonText: 'View Details',
          showCancelButton: true,
          cancelButtonText: 'Stay on Dashboard'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate(`/agent/cibil/${response.data.cibil_id}`);
          } else {
            fetchLeads();
          }
        });
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'CIBIL Check Failed',
          text: response.data.message || 'Unable to fetch CIBIL data for this lead'
        });
      }
    } catch (error) {
      console.error('Error checking CIBIL:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response && error.response.data && error.response.data.detail ? error.response.data.detail : 'Failed to check CIBIL score. Please try again.'
      });
    }
  };
  
  useEffect(() => {
    console.log('Current leads state:', leads);
    if (leads && leads.length > 0) {
      leads.forEach((lead, index) => {
        console.log(`Lead ${index} data:`, JSON.stringify(lead));
      });
    }
  }, [leads]);
  
  return (
    <div className="agent-dashboard-layout">
      <AgentSidebar />
      
      <div className="agent-content-wrapper">
        <header className="agent-header">
          <div className="agent-greeting">
            <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
          </div>

          <div ref={searchRef} className="agent-header-search">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search leads, products, invoices..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
              {isSearching && (
                <Spinner 
                  animation="border" 
                  size="sm" 
                  className="search-spinner" 
                />
              )}
            </div>
            
            {/* Search results dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                <SearchResults 
                  results={searchResults}
                  onResultClick={handleSearchResultClick}
                />
              </div>
            )}
          </div>

          <div className="agent-header-actions">


            <div className="agent-user-profile">
              <div className="agent-avatar">
                <FaUser />
              </div>
              <div className="agent-user-info">
                <span className="agent-user-name">
                  {currentUser?.name || 'Agent User'}
                </span>
                <button className="agent-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="agent-main-content">
          {/* Dashboard Welcome Section */}
          <div className="agent-dashboard-welcome">
            <h1>Dashboard</h1>
            <p>Manage your leads and track performance</p>
          </div>
          
          {/* Dashboard Stats Grid */}
          <div className="agent-stats-grid">
            <div className="agent-stat-card">
              <div className="agent-stat-icon" style={{backgroundColor: '#ecf0ff'}}>
                <FaClipboardList style={{color: '#3b82f6'}} />
              </div>
              <div className="agent-stat-details">
                <p className="agent-stat-title">Active Leads</p>
                <h2 className="agent-stat-value">
                  {loading ? <Spinner size="sm" animation="border" /> : (summaryData.total_active_leads || 0)}
                </h2>
                <p className="agent-stat-change">Current Month</p>
              </div>
            </div>

            <div className="agent-stat-card">
              <div className="agent-stat-icon" style={{backgroundColor: '#e6f9ff'}}>
                <FaExchangeAlt style={{color: '#0ea5e9'}} />
              </div>
              <div className="agent-stat-details">
                <p className="agent-stat-title">Total Applications</p>
                <h2 className="agent-stat-value">
                  {loading ? <Spinner size="sm" animation="border" /> : (summaryData.total_applications || 0)}
                </h2>
                <p className="agent-stat-change">Current Month</p>
              </div>
            </div>

            <div className="agent-stat-card">
              <div className="agent-stat-icon" style={{backgroundColor: '#e6ffec'}}>
                <FaCheckCircle style={{color: '#10b981'}} />
              </div>
              <div className="agent-stat-details">
                <p className="agent-stat-title">Disbursements</p>
                <h2 className="agent-stat-value">
                  {loading ? <Spinner size="sm" animation="border" /> : (summaryData.total_disbursement || 0)}
                </h2>
                <p className="agent-stat-change">Current Month</p>
              </div>
            </div>

            <div className="agent-stat-card">
              <div className="agent-stat-icon" style={{backgroundColor: '#fff8e6'}}>
                <FaRupeeSign style={{color: '#f59e0b'}} />
              </div>
              <div className="agent-stat-details">
                <p className="agent-stat-title">Total Earnings</p>
                <h2 className="agent-stat-value">
                  {loading ? <Spinner size="sm" animation="border" /> : formatCurrency(summaryData.total_earnings || 0)}
                </h2>
                <p className="agent-stat-change">Current Month</p>
              </div>
            </div>
          </div>

          {/* Recent Leads Card */}
          <div className="agent-dashboard-card">
            <div className="agent-card-header d-flex justify-content-between align-items-center">
              <h3>Recent Leads</h3>
              <div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowAddLeadModal(true)}
                  className="me-2"
                >
                  <FaPlus className="me-1" /> Add Lead
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowBulkUploadModal(true)}
                >
                  <FaFileUpload className="me-1"/> Bulk Upload
                </Button>
              </div>
            </div>
            <div className="agent-card-body">
              {loadingLeads ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2">Loading leads...</p>
                </div>
              ) : leads.length > 0 ? (
                <div className="table-responsive">
                  <table className="agent-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>Product</th>
                        <th>Status</th>
                        <th>CIBIL</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead, index) => {
                        // Get user data regardless of structure
                        const userName = lead.user ? lead.user.name : (lead.lead_user ? lead.lead_user.name : 'Unknown');
                        const userPhone = lead.user ? lead.user.mobile : (lead.lead_user ? lead.lead_user.phone || lead.lead_user.mobile : 'Unknown');
                        const userEmail = lead.user ? lead.user.email : (lead.lead_user ? lead.lead_user.email : 'Unknown');
                        
                        // Get CIBIL score - check all possible locations
                        const cibilScore = lead.cibil_score || 
                                          (lead.lead_user && lead.lead_user.cibil_score) || 
                                          (lead.user && lead.user.cibil_score);
                        
                        // Get product name
                        const productName = lead.product ? lead.product.name : 'Unknown';
                        
                        // Get status information
                        const statusCode = lead.status && lead.status.code ? parseInt(lead.status.code) : 0;
                        const statusName = lead.status && lead.status.name ? lead.status.name : 
                                          (lead.status_name ? lead.status_name : 'Unknown');
                        
                        // Get status badge class
                        const getStatusBadgeClass = (code) => {
                          code = parseInt(code);
                          switch (code) {
                            case 1: return 'new'; // Requested
                            case 2: return 'processing'; // In Progress
                            case 3: return 'completed'; // Disbursed
                            case 4: return 'rejected'; // Rejected
                            default: return ''; 
                          }
                        };
                        
                        return (
                          <tr key={lead.id}>
                            <td>{index + 1 + filters.skip}</td>
                            <td>{userName}</td>
                            <td>
                              {userPhone && <div><FaPhone className="me-1" size={12} /> {userPhone}</div>}
                              {userEmail && <div><FaEnvelope className="me-1" size={12} /> {userEmail}</div>}
                            </td>
                            <td>{productName}</td>
                            <td>
                              <span className={`agent-status-badge ${getStatusBadgeClass(statusCode)}`}>
                                {statusName}
                              </span>
                            </td>
                            <td>
                              {cibilScore ? (
                                <Badge 
                                  as={Link}
                                  to={`/agent/cibil/${lead.lead_user.cibil_id}`}
                                  bg={getCibilScoreBadgeColor(cibilScore)}
                                  style={{ cursor: 'pointer', textDecoration: 'none' }}
                                >
                                  {cibilScore}
                                  </Badge>
                              ) : (
                                <Button 
                                  size="sm" 
                                  variant="outline-secondary"
                                  onClick={() => handleCheckCibilRequest(lead.id)}
                                  disabled={lead.checking_cibil}
                                >
                                  {lead.checking_cibil ? (
                                    <>
                                      <Spinner 
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-1"
                                      />
                                      Checking...
                                    </>
                                  ) : (
                                    <>Check CIBIL</>
                                  )}
                                </Button>
                              )}
                            </td>
                            <td>
                              <Button 
                                size="sm" 
                                variant="outline-primary"
                                onClick={() => {
                                  navigate(`/agent/lead/${lead.lead_profile.id}`);
                                }}
                              >
                                <FaFileAlt className="me-1" /> View
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="mb-2">No leads found</p>
                  <div className="d-flex justify-content-center gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowAddLeadModal(true)}
                    >
                      <FaPlus className="me-1" /> Add Lead
                    </Button>
                    <Button
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => setShowBulkUploadModal(true)}
                    >
                      <FaFileUpload className="me-1"/> Bulk Upload
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Pagination */}
              {leads.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <div>
                    Showing {filters.skip + 1} to {Math.min(filters.skip + leads.length, totalLeads)} of {totalLeads} entries
                  </div>
                  <div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={filters.skip === 0}
                      onClick={() => handlePageChange(filters.skip - filters.limit)}
                      className="me-2"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={filters.skip + filters.limit >= totalLeads}
                      onClick={() => handlePageChange(filters.skip + filters.limit)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Add Lead Modal */}
      <Modal
        show={showAddLeadModal}
        onHide={() => setShowAddLeadModal(false)}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Form noValidate validated={validated} onSubmit={handleAddLead}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Lead</Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            {addLeadError && (
              <Alert variant="danger" className="mb-3">
                {addLeadError}
              </Alert>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>
                <FaUser className="me-1" /> Full Name
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Enter full name"
                value={newLead.name}
                onChange={handleNewLeadChange}
                required
              />
              <Form.Control.Feedback type="invalid">
                Please enter a name.
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>
                <FaEnvelope className="me-1" /> Email Address
              </Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder="Enter email address"
                value={newLead.email}
                onChange={handleNewLeadChange}
                required
              />
              <Form.Control.Feedback type="invalid">
                Please enter a valid email.
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>
                <FaPhone className="me-1" /> Mobile Number
              </Form.Label>
              <Form.Control
                type="tel"
                name="mobile"
                placeholder="Enter 10-digit mobile number"
                value={newLead.mobile}
                onChange={handleNewLeadChange}
                pattern="[0-9]{10}"
                required
              />
              <Form.Control.Feedback type="invalid">
                Please enter a valid 10-digit mobile number.
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Case Type</Form.Label>
              <Form.Select
                name="case_type"
                value={newLead.case_type}
                onChange={handleNewLeadChange}
                required
              >
                <option value="1">Credit Card</option>
                <option value="2">Personal Loan</option>
                <option value="3">Home Loan</option>
                <option value="4">Business Loan</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Product</Form.Label>
              <Select
                className="basic-select"
                classNamePrefix="select"
                isSearchable
                isClearable
                isLoading={loadingProducts}
                placeholder="Select product"
                name="product_id"
                options={products.map(product => ({
                  value: product.id.toString(),
                  label: product.name
                }))}
                onChange={(option) => handleSelectChange('product_id', option)}
                required
              />
              <div className="invalid-feedback d-block">
                {!newLead.product_id && validated ? 'Please select a product.' : ''}
              </div>
            </Form.Group>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowAddLeadModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={addingLead}
            >
              {addingLead ? (
                <React.Fragment>
                  <Spinner 
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-1"
                  />
                  <span className="ms-1">Adding...</span>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <FaPlus className="me-1" /> Add Lead
                </React.Fragment>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Search Modal for Mobile */}
      <Modal 
        show={showSearchModal} 
        onHide={() => setShowSearchModal(false)}
        className="search-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Search Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mobile-search-container mb-3">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search leads, products, invoices..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                autoFocus
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          
          {isSearching ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-3">Searching...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <SearchResults 
              results={searchResults} 
              onResultClick={handleSearchResultClick} 
            />
          ) : searchQuery.trim().length > 1 ? (
            <div className="text-center py-5">
              <p>No results found for "{searchQuery}".</p>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <FaSearch size={32} />
              <p className="mt-3">Enter at least 2 characters to search</p>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSubmit={handleBulkUploadSubmit}
        fixedAgentId={currentUser?.agent_profile?.id || currentUser?.id}
        fixedAgentName={currentUser?.name}
      />
    </div>
  );
};

export default AgentDashboard;
